import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, Video, Upload, Trash2, ArrowLeft, CheckCircle2, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { getToken, clearToken } from '../lib/adminApi';
import { listGallery, uploadGalleryPhoto, addGalleryVideo, deleteGalleryItem } from '../lib/galleryApi';
import { galleryCategories } from '../data/gallery';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminGallery() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  // Photo form
  const [pFile, setPFile] = useState(null);
  const [pTitle, setPTitle] = useState('');
  const [pCat, setPCat] = useState('campus');

  // Video form
  const [vTitle, setVTitle] = useState('');
  const [vId, setVId] = useState('');
  const [vCat, setVCat] = useState('campus');

  const refresh = async () => {
    try { setItems(await listGallery()); }
    catch (err) { if (err.status === 401) { clearToken(); nav('/admin/login', { replace: true }); } }
  };
  useEffect(() => {
    if (!getToken()) { nav('/admin/login', { replace: true }); return; }
    refresh();
    // eslint-disable-next-line
  }, []);

  const submitPhoto = async (e) => {
    e.preventDefault();
    if (!pFile) { setMsg({ type: 'err', text: 'Choose a photo file first' }); return; }
    setBusy(true); setMsg(null);
    try {
      await uploadGalleryPhoto(pFile, { title: pTitle, category: pCat });
      setPFile(null); setPTitle('');
      setMsg({ type: 'ok', text: 'Photo uploaded' });
      refresh();
    } catch (err) { setMsg({ type: 'err', text: err.message }); }
    finally { setBusy(false); }
  };

  const submitVideo = async (e) => {
    e.preventDefault();
    if (!vTitle || !vId) { setMsg({ type: 'err', text: 'Title + YouTube ID required' }); return; }
    setBusy(true); setMsg(null);
    try {
      await addGalleryVideo({ title: vTitle, youtube_id: vId, category: vCat });
      setVTitle(''); setVId('');
      setMsg({ type: 'ok', text: 'Video added' });
      refresh();
    } catch (err) { setMsg({ type: 'err', text: err.message }); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try { await deleteGalleryItem(id); refresh(); }
    catch (err) { setMsg({ type: 'err', text: err.message }); }
  };

  return (
    <div className="bg-muted/40 min-h-[calc(100vh-80px)]" data-testid="admin-gallery">
      <SEO title="Admin — Gallery | Russian Education" description="Manage gallery" noindex />
      <div className="bg-primary text-white">
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-6 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand text-white flex items-center justify-center shadow-lg shadow-brand/40">
              <ImageIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/80 font-semibold">Admin</div>
              <h1 className="font-serif text-xl md:text-2xl font-bold">Gallery — Photos & Videos</h1>
            </div>
          </div>
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm font-semibold bg-white/10 hover:bg-white/20 border border-white/30 h-10 px-4 rounded-md">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 md:px-8 py-8 space-y-6">
        {msg && (
          <div data-testid="gallery-feedback" className={`p-4 rounded-xl flex items-start gap-2 text-sm ${msg.type === 'ok' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-brand/10 border border-brand/30 text-brand'}`}>
            {msg.type === 'ok' ? <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" /> : <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />}
            <span>{msg.text}</span>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-5">
          {/* PHOTO UPLOAD */}
          <form onSubmit={submitPhoto} className="bg-white rounded-2xl border border-border p-6 space-y-3" data-testid="photo-form">
            <div className="flex items-center gap-2 text-brand font-bold text-xs uppercase tracking-widest">
              <Camera className="w-4 h-4" /> Upload Photo
            </div>
            <input
              data-testid="photo-title"
              value={pTitle}
              onChange={(e) => setPTitle(e.target.value)}
              placeholder="Title (optional)"
              className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand"
            />
            <select
              data-testid="photo-category"
              value={pCat}
              onChange={(e) => setPCat(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand bg-white"
            >
              {galleryCategories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <label className="flex items-center gap-2 w-full h-11 px-3 rounded-lg border border-dashed border-border cursor-pointer hover:border-brand/50 text-sm">
              <Upload className="w-4 h-4 text-brand" />
              <span className="flex-1 truncate">{pFile ? pFile.name : 'Choose PNG / JPG / WEBP · max 5 MB'}</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                data-testid="photo-file"
                onChange={(e) => setPFile(e.target.files?.[0] || null)}
              />
            </label>
            <Button type="submit" disabled={busy} data-testid="photo-submit" className="w-full h-11 bg-brand hover:bg-brand-dark text-white font-bold">
              {busy ? 'Uploading…' : 'Upload Photo'}
            </Button>
          </form>

          {/* VIDEO ADD */}
          <form onSubmit={submitVideo} className="bg-white rounded-2xl border border-border p-6 space-y-3" data-testid="video-form">
            <div className="flex items-center gap-2 text-brand font-bold text-xs uppercase tracking-widest">
              <Video className="w-4 h-4" /> Add YouTube Video
            </div>
            <input
              data-testid="video-title"
              value={vTitle}
              onChange={(e) => setVTitle(e.target.value)}
              placeholder="Video title"
              className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand"
              required
            />
            <input
              data-testid="video-id"
              value={vId}
              onChange={(e) => setVId(e.target.value)}
              placeholder="YouTube ID (e.g. dQw4w9WgXcQ from youtube.com/watch?v=…)"
              className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand"
              required
            />
            <select
              data-testid="video-category"
              value={vCat}
              onChange={(e) => setVCat(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand bg-white"
            >
              {galleryCategories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <Button type="submit" disabled={busy} data-testid="video-submit" className="w-full h-11 bg-primary hover:bg-primary/90 text-white font-bold">
              {busy ? 'Adding…' : 'Add Video'}
            </Button>
          </form>
        </div>

        {/* Existing items */}
        <div className="bg-white rounded-2xl border border-border p-6">
          <div className="font-serif font-bold text-primary text-lg mb-4" data-testid="gallery-count">
            Items ({items.length})
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No items uploaded yet. Add your first photo or video above.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-gallery-grid">
              {items.map((it) => (
                <div key={it.id} className="relative rounded-xl overflow-hidden border border-border" data-testid={`admin-gallery-item-${it.id}`}>
                  <img
                    src={it.kind === 'video' ? it.poster : `${BASE}${it.src}`}
                    alt={it.title}
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute top-1.5 left-1.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest text-white ${it.kind === 'video' ? 'bg-brand' : 'bg-primary'}`}>
                      {it.kind}
                    </span>
                  </div>
                  <button
                    onClick={() => del(it.id)}
                    data-testid={`admin-gallery-delete-${it.id}`}
                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/95 hover:bg-brand hover:text-white text-brand flex items-center justify-center transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <div className="p-2 bg-white">
                    <div className="text-xs font-bold text-primary line-clamp-1">{it.title}</div>
                    <div className="text-[10px] text-muted-foreground">{it.category}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground text-center">
          Photos served at <code className="text-brand">/gallery/&lt;id&gt;.&lt;ext&gt;</code> · Videos embedded via YouTube ID (extract from <code>youtube.com/watch?v=<b>ID</b></code>)
        </p>
      </div>
    </div>
  );
}
